import { useEffect, useState } from "react";
import { getCountryName } from "../../countries";
import styles from "../Profile.module.css";
import AddProfilePic from "./AddProfilePic";
import Popup from "../../../components/ui/popup";
import Link from "next/link";
// import { FaCameraRetro } from 'react-icons/fa';
import { FaFacebookF } from "react-icons/fa";
import { FaUpload } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { FaInfinity } from "react-icons/fa";
import useTranslation from "next-translate/useTranslation";
import {
  DAYLY_LEAGUE_GAMES,
  DAYLY_ANALYZES,
  GAMES_DATABASE,
  TIERS,
  UNLIMITED_TIER,
  FREE_TIER,
  AvatarTypes,
} from "@chesshotel/constants";
import { setMembership, useMembership } from "../../../redux/membership";
import { useUser, setAvatarId, setAvatarUpload } from "../../../redux/user";
import UserAvatar from "../../UserAvatar";
import { setLoadingBar } from "../../../redux/siteData";
import { api } from "../../../services/api";
import FBScript from "@/components/util/fb-script";
import { Button } from "@/components/ui/button";

export function updateAvatarId(avatarId: number) {
  setLoadingBar(true);
  api
    .post("/users/avatarId", { avatarId })
    .then(() => {
      setAvatarId(avatarId);
    })
    .catch(() => {
      console.error("updateAvatarId fail");
    })
    .then(() => {
      console.log("updateAvatarId done");
      setLoadingBar(false);
    });
}

export function uploadAvatar(data: FormData) {
  setLoadingBar(true);
  api
    .post("/users/avatar-image", data)
    .then(() => {
      setAvatarUpload();
    })
    .catch(() => {})
    .then(() => {
      setLoadingBar(false);
    });
}

export default function Membership() {
  const { t } = useTranslation("common");
  const { t: authT } = useTranslation("auth");
  const { t: commonT } = useTranslation("common");

  const { username, created, country, avatarType } = useUser();
  const { tier, endDate } = useMembership();

  function formatDate(value?: string | Date | null) {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return "";
    }

    return (
      date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
    );
  }

  const dateStr = formatDate(created);
  const endDateStr = formatDate(endDate);

  const [showAddProfilePic, setShowAddProfilePic] = useState(false);
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const [cancelStatus, setCancelStatus] = useState<
    "idle" | "loading" | "scheduled" | "error"
  >("idle");
  const [cancelMessage, setCancelMessage] = useState("");

  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [formData, setFormData] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);

  useEffect(() => {
    if (avatarType !== AvatarTypes.UPLOAD) {
      setImagePreviewUrl(null);
    }
  }, [avatarType]);

  const tierStr = TIERS[tier];
  const leagueGamesStr =
    tier === UNLIMITED_TIER ? <FaInfinity /> : DAYLY_LEAGUE_GAMES[tier];
  const analyzesStr =
    tier === UNLIMITED_TIER ? <FaInfinity /> : DAYLY_ANALYZES[tier];
  const gamesDatabaseStr =
    tier === UNLIMITED_TIER ? <FaInfinity /> : commonT(GAMES_DATABASE[tier]);

  async function deletePlan() {
    setCancelStatus("loading");
    setCancelMessage("");
    setLoadingBar(true);

    try {
      const response = await api.get("/stripe/delete");
      const message =
        response?.data?.message || authT("Subscription cancellation scheduled");

      setCancelStatus("scheduled");
      setCancelMessage(message);
      setShowAreYouSure(false);

      try {
        const membershipResponse = await api.get("/membership");
        if (membershipResponse?.data?.membership) {
          setMembership(membershipResponse.data.membership);
        }
      } catch (error) {
        console.error("refresh membership after cancellation failed");
      }
    } catch (error) {
      setCancelStatus("error");
      setCancelMessage(t("Something went wrong"));
    } finally {
      setLoadingBar(false);
    }
  }

  function handleImageChange(e) {
    e.preventDefault();

    const reader = new FileReader();
    const file = e.target.files[0];

    reader.onloadend = async () => {
      const str: string = reader.result.toString();
      setImagePreviewUrl(str);

      const data = new FormData();
      data.append("file", file);
      setFormData(data);
      setUploadOpen(true);
    };

    reader.readAsDataURL(file);
  }

  function renderAreYouSure() {
    return (
      <div className={styles.areYouSure}>
        {t("Are you sure?")}
        <div className={styles.yesNo}>
          <button
            className="btn btn-base btn-blue"
            disabled={cancelStatus === "loading"}
            onClick={() => deletePlan()}
          >
            {cancelStatus === "loading" ? t("Loading...") : t("Yes")}
          </button>
          <button
            className="ml-2 btn btn-base btn-outline"
            disabled={cancelStatus === "loading"}
            onClick={() => setShowAreYouSure(false)}
          >
            {t("No")}
          </button>
        </div>
      </div>
    );
  }

  function renderButtons() {
    if (tier === FREE_TIER) {
      return (
        <div className={styles.buttonCont}>
          <Link href="/memberships">
            <button className="btn btn-base btn-red">{t("Get premium")}</button>
          </Link>
        </div>
      );
    } else {
      return (
        <div className={styles.buttonCont}>
          <Link href="/memberships">
            <button className="btn btn-base btn-blue">
              {authT("Change membership")}
            </button>
          </Link>

          <a href="https://billing.stripe.com/p/login/9AQaFk4qu5ug8ve6oo">
            <button className="btn btn-base btn-blue">
              {authT("Manage subscription")}
            </button>
          </a>

          {cancelStatus !== "scheduled" && (
            <button
              className="btn btn-base btn-outline"
              onClick={() => setShowAreYouSure(true)}
            >
              {authT("Cancel membership")}
            </button>
          )}
        </div>
      );
    }
  }

  function renderDefaultAvatars() {
    const avatarIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    return (
      <Popup
        title={authT("Choose avatar")}
        onClose={() => setAvatarOpen(false)}
      >
        <div className="flex flex-wrap">
          {avatarIds.map((avatarId) => {
            return (
              <div
                className={`relative m-4 rounded-full cursor-pointer hover:opacity-70`}
                onClick={() => {
                  setAvatarOpen(false);
                  updateAvatarId(avatarId);
                }}
                key={avatarId}
              >
                <img
                  className={`inline-block w-20 h-20`}
                  src={`/static/avatars/${avatarId}.svg`}
                  alt="avatar"
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-end items-center pt-4 mt-4 w-full">
          <button
            className="btn btn-base btn-outline"
            onClick={() => setAvatarOpen(false)}
          >
            {t("Cancel")}
          </button>
        </div>
      </Popup>
    );
  }

  function renderUploadModal() {
    function handleOnClose() {
      setFormData(null);
      setImagePreviewUrl(null);
      setUploadOpen(false);
    }

    return (
      <Popup title={authT("Upload image?")} onClose={handleOnClose}>
        <Button
          variant="default"
          size="wide"
          onClick={() => {
            handleOnClose();
            uploadAvatar(formData);
          }}
        >
          {authT("Upload image")}
        </Button>
        <Button
          variant="outline"
          size="wide"
          className="ml-3"
          onClick={() => handleOnClose()}
        >
          {t("Cancel")}
        </Button>
      </Popup>
    );
  }

  return (
    <div className={styles.statsBody}>
      <FBScript />
      {showAddProfilePic && (
        <AddProfilePic onClose={() => setShowAddProfilePic(false)} />
      )}
      {uploadOpen && renderUploadModal()}
      {avatarOpen && renderDefaultAvatars()}
      <div className={styles.stats}>
        <div className={styles.avatarCont}>
          <div className={styles.avatar}>
            {imagePreviewUrl ? (
              <img
                className="w-32 h-32 rounded-full border border-gray-200"
                src={imagePreviewUrl}
              />
            ) : (
              <UserAvatar className="w-32 h-32" />
            )}
          </div>
          <div className="flex items-center self-end p-2 space-x-1">
            {/* <IconButton
              cursor="pointer" onClick={() => { }}
              size="xs" aria-label={'Webcam'} icon={<FaCameraRetro />} >
            </IconButton> */}
            <span className="has-tooltip">
              <span className="tooltip">{authT("Upload image")}</span>
              <label
                htmlFor="file-upload"
                className="bg-gray-100 cursor-pointer btn btn-icon btn-icon-sm btn-goast"
              >
                <FaUpload />
              </label>
            </span>
            <input
              style={{ display: "none" }}
              id="file-upload"
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={(e) => handleImageChange(e)}
            />
            <span className="has-tooltip">
              <span className="tooltip">{authT("Choose avatar")}</span>
              <button
                className="bg-gray-100 btn btn-icon btn-icon-sm btn-goast"
                onClick={() => setAvatarOpen(true)}
              >
                <FaUser />
              </button>
            </span>
            <span className="has-tooltip">
              <span className="tooltip">{authT("Choose facebook avatar")}</span>
              <button
                className="bg-gray-100 btn btn-icon btn-icon-sm btn-goast"
                onClick={() => setShowAddProfilePic(true)}
              >
                <FaFacebookF />
              </button>
            </span>
          </div>
          <div className={styles.username}>{username}</div>
        </div>
        <div className={styles.userInfo}>
          {created && (
            <div className={styles.infoCont}>
              <div className={styles.infoTitle}>{authT("Member since")}</div>
              <div className={styles.infoValue}>{dateStr}</div>
            </div>
          )}
          {/* {country && (
            <div className={styles.infoCont}>
              <div className={styles.infoTitle}>{t("Country")}</div>
              <div className={styles.infoValue}>{getCountryName(country)}</div>
            </div>
          )} */}
          <div className={styles.infoCont}>
            <div className={styles.infoTitle}>
              {authT("Current membership")}
            </div>
            <div className={styles.infoValue}>{tierStr}</div>
          </div>
          {cancelStatus === "scheduled" && endDateStr && (
            <div className={styles.infoCont}>
              <div className={styles.infoTitle}>
                {authT("Membership ends on")}
              </div>
              <div className={styles.infoValue}>{endDateStr}</div>
            </div>
          )}
          <div className={styles.infoCont}>
            <div className={styles.infoTitle}>
              {authT("League games / day")}
            </div>
            <div className={styles.infoValue}>{leagueGamesStr}</div>
          </div>
          <div className={styles.infoCont}>
            <div className={styles.infoTitle}>
              {commonT("Game reviews / day")}
            </div>
            <div className={styles.infoValue}>{analyzesStr}</div>
          </div>
          <div className={styles.infoCont}>
            <div className={styles.infoTitle}>{authT("Games database")}</div>
            <div className={styles.infoValue}>{gamesDatabaseStr}</div>
          </div>
        </div>
      </div>
      {renderButtons()}
      {cancelStatus === "scheduled" && (
        <div className="px-4 pb-2 text-xs text-green-700">{cancelMessage}</div>
      )}
      {cancelStatus === "error" && (
        <div className="px-4 pb-2 text-xs text-red-700">{cancelMessage}</div>
      )}
      {showAreYouSure && renderAreYouSure()}
    </div>
  );
}
